// ============================================================
// Page Audit Logs Super Admin — Lot L23 (frontend)
// ============================================================
// Vue globale (tous commerces) des actions sensibles :
//   - Filtres : commerce, action, dates, user_id, super_admin_id
//   - Tableau paginé
//   - Export CSV (avec trace dans exports_logs)
// ============================================================

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import superAdminApi from '../../services/superAdminApi';
import {
  ScrollText, Search, RefreshCw, Download, ChevronLeft, ChevronRight,
  User, Shield, Loader,
} from 'lucide-react';
import toast from 'react-hot-toast';

const PAR_PAGE = 50;

function formatDateHeure(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

function couleurAction(action) {
  if (!action) return 'text-slate-500';
  if (/cancel|delete|annul|desactiv|reset|revok|suppr/i.test(action)) return 'text-red-600';
  if (/create|add|new/i.test(action))                                 return 'text-emerald-700';
  if (/update|modif|edit|change/i.test(action))                       return 'text-amber-700';
  if (/login|auth|2fa/i.test(action))                                 return 'text-blue-700';
  return 'text-slate-700';
}

export default function SuperAdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [commerces, setCommerces] = useState([]);
  const [selected, setSelected] = useState(null);  // log focus pour détail JSON

  // Filtres
  const [filtres, setFiltres] = useState({
    commerce_id: '',
    action: '',
    entite_type: '',
    date_debut: '',
    date_fin: '',
  });

  const params = useMemo(() => {
    const p = { page, limit: PAR_PAGE };
    Object.entries(filtres).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [page, filtres]);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await superAdminApi.get('/superadmin/audit-logs', { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec du chargement');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    superAdminApi.get('/superadmin/commerces')
      .then((r) => setCommerces(r.data.commerces || r.data || []))
      .catch(() => {});
  }, []);

  const setFiltre = (k, v) => {
    setFiltres((f) => ({ ...f, [k]: v }));
    setPage(1);
  };

  const reset = () => {
    setFiltres({ commerce_id: '', action: '', entite_type: '', date_debut: '', date_fin: '' });
    setPage(1);
  };

  const exporter = async () => {
    setExporting(true);
    try {
      const res = await superAdminApi.get('/superadmin/audit-logs/export.csv', {
        params: { ...params, page: undefined, limit: undefined },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-global-${new Date().toISOString().substring(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (err) {
      toast.error('Échec de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAR_PAGE));

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
            <ScrollText size={18} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Audit logs — Plateforme</h1>
            <p className="text-xs text-slate-400">
              Actions sensibles sur tous les commerces. Export CSV tracé.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={charger}
            className="p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 rounded-lg"
            title="Rafraîchir"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={exporter}
            disabled={exporting || logs.length === 0}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700
                       disabled:opacity-40 text-slate-900 text-sm font-medium rounded-lg"
          >
            {exporting ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
            CSV
          </button>
        </div>
      </header>

      {/* Filtres */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Commerce</label>
          <select
            value={filtres.commerce_id}
            onChange={(e) => setFiltre('commerce_id', e.target.value)}
            className="w-full bg-slate-100 border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900"
          >
            <option value="">Tous</option>
            {commerces.map((c) => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Action</label>
          <input
            type="text"
            value={filtres.action}
            onChange={(e) => setFiltre('action', e.target.value)}
            placeholder="ex: user.create"
            className="w-full bg-slate-100 border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Type d'entité</label>
          <input
            type="text"
            value={filtres.entite_type}
            onChange={(e) => setFiltre('entite_type', e.target.value)}
            placeholder="ex: user, vente"
            className="w-full bg-slate-100 border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Du</label>
          <input
            type="date"
            value={filtres.date_debut}
            onChange={(e) => setFiltre('date_debut', e.target.value)}
            className="w-full bg-slate-100 border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Au</label>
          <input
            type="date"
            value={filtres.date_fin}
            onChange={(e) => setFiltre('date_fin', e.target.value)}
            className="w-full bg-slate-100 border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900"
          />
        </div>
        <div className="sm:col-span-3 lg:col-span-5 flex justify-end">
          <button
            onClick={reset}
            className="text-xs text-slate-500 hover:text-slate-900 px-3 py-1"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs text-slate-500">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Commerce</th>
                <th className="px-3 py-2 font-medium">Acteur</th>
                <th className="px-3 py-2 font-medium">Action</th>
                <th className="px-3 py-2 font-medium">Entité</th>
                <th className="px-3 py-2 font-medium">IP</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center text-slate-400 py-8">
                  <Loader size={20} className="animate-spin mx-auto" />
                </td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-slate-400 py-8">
                  Aucun log ne correspond aux filtres
                </td></tr>
              ) : (
                logs.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => setSelected(l)}
                    className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                      {formatDateHeure(l.created_at)}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {l.commerce_nom || (l.commerce_id ? l.commerce_id.substring(0, 8) : '—')}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {l.super_admin_email ? (
                        <span className="flex items-center gap-1">
                          <Shield size={11} className="text-blue-600" /> {l.super_admin_email}
                        </span>
                      ) : l.user_email ? (
                        <span className="flex items-center gap-1">
                          <User size={11} className="text-slate-400" /> {l.user_email}
                        </span>
                      ) : '—'}
                    </td>
                    <td className={`px-3 py-2 font-mono text-xs ${couleurAction(l.action)}`}>
                      {l.action}
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs">
                      {l.entite_type}{l.entite_id ? ` · ${l.entite_id.substring(0, 8)}` : ''}
                    </td>
                    <td className="px-3 py-2 text-slate-400 text-xs font-mono">
                      {l.ip || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 text-xs text-slate-500">
          <span>
            {total.toLocaleString('fr-FR')} entrées · page {page} / {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Modale détail */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white border border-slate-300 rounded-xl max-w-2xl w-full p-5 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Search size={16} /> Détail du log
              </h3>
              <button
                onClick={() => setSelected(null)}
                className="text-slate-500 hover:text-slate-900 text-sm"
              >
                Fermer
              </button>
            </div>
            <dl className="space-y-2 text-sm">
              <Detail label="Date"     value={formatDateHeure(selected.created_at)} />
              <Detail label="Action"   value={<span className={couleurAction(selected.action) + ' font-mono'}>{selected.action}</span>} />
              <Detail label="Commerce" value={selected.commerce_nom || selected.commerce_id || '—'} />
              <Detail label="Acteur"   value={selected.user_email || selected.super_admin_email || '—'} />
              <Detail label="Entité"   value={`${selected.entite_type || '—'}${selected.entite_id ? ' · ' + selected.entite_id : ''}`} />
              <Detail label="IP"       value={selected.ip || '—'} />
              <Detail label="Motif"    value={selected.motif || '—'} />
            </dl>
            {selected.anciennes_valeurs && (
              <details className="mt-3">
                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-900">Anciennes valeurs</summary>
                <pre className="mt-2 bg-slate-100 rounded p-2 text-xs text-slate-700 overflow-x-auto">
                  {JSON.stringify(selected.anciennes_valeurs, null, 2)}
                </pre>
              </details>
            )}
            {selected.nouvelles_valeurs && (
              <details className="mt-3" open>
                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-900">Nouvelles valeurs</summary>
                <pre className="mt-2 bg-slate-100 rounded p-2 text-xs text-slate-700 overflow-x-auto">
                  {JSON.stringify(selected.nouvelles_valeurs, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="text-xs text-slate-400 w-24 shrink-0">{label}</dt>
      <dd className="text-sm text-slate-700">{value}</dd>
    </div>
  );
}
